/**
 * pushNotifications.ts — Web Push subscription helpers for UniUI PWA.
 *
 * Flow:
 *  1. On_app_load, call `initPushNotifications()` after the user logs in.
 *  2. This requests Notification permission, subscribes via VAPID Web Push,
 *     and registers the subscription with the backend.
 *  3. The backend can then send push messages at any time via /push/broadcast.
 */

import { getBackendHeaders } from "@/lib/api";

const BACKEND_URL = `${process.env.NEXT_PUBLIC_API_URL || "https://api.uniui.com.ng"}/api/v1`;

// --- VAPID Key -------------------------------------------------------------

/**
 * Convert a base64url VAPID public key to a Uint8Array (required by
 * PushManager.subscribe).
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  try {
    const clean = (base64String || "").trim();
    const padding = "=".repeat((4 - (clean.length % 4)) % 4);
    const base64 = (clean + padding).replace(/-/g, "+").replace(/_/g, "/");
    // atob throws InvalidCharacterError on malformed input (e.g. a VAPID key
    // that wasn't base64url, or an empty/missing value). Degrade silently so
    // push subscription failure never crashes sign-in / OAuth — the caller's
    // try/catch will treat the empty key as "push unavailable".
    const rawData = window.atob(base64);
    return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
  } catch {
    return new Uint8Array(0);
  }
}

async function fetchVapidPublicKey(authToken?: string): Promise<string> {
  try {
    const headers = getBackendHeaders(authToken?.replace(/^Bearer\s+/i, ""));
    const res = await fetch(`${BACKEND_URL}/push/vapid-key`, { headers });
    if (!res.ok) throw new Error("VAPID key unavailable");
    const json = await res.json();
    return json.vapid_public_key as string;
  } catch {
    return "";
  }
}

// --- Subscribe --------------------------------------------------------------

/**
 * Subscribe the current browser to web push and register with backend.
 * Returns true on success, false if push is not supported or denied.
 */
export async function subscribeToPush(authToken: string): Promise<boolean> {
  if (typeof window === "undefined") return false;
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    console.info("[Push] Web Push not supported in this browser");
    return false;
  }

  // 1. Request notification permission
  let permission = Notification.permission;
  if (permission === "default") {
    permission = await Notification.requestPermission();
  }
  if (permission !== "granted") {
    console.info("[Push] Notification permission denied");
    return false;
  }

  // 2. Get the service worker registration
  const registration = await navigator.serviceWorker.ready;

  // 3. Fetch VAPID public key
  const vapidPublicKey = await fetchVapidPublicKey(authToken);
  if (!vapidPublicKey) {
    console.warn("[Push] VAPID public key not available");
    return false;
  }

  // 4. Subscribe_or_reuse_existing_subscription
  let subscription: PushSubscription;
  try {
    const existing = await registration.pushManager.getSubscription();
    if (existing) {
      subscription = existing;
    } else {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(
          vapidPublicKey,
        ) as BufferSource,
      });
    }
  } catch (err) {
    console.error("[Push] Failed_to_subscribe:", err);
    return false;
  }

  // 5. Register with backend
  try {
    const sub = subscription.toJSON();
    const body = JSON.stringify({
      token: sub.endpoint,
      platform: "web",
      endpoint: sub.endpoint,
      subscription_json: JSON.stringify(sub),
    });

    const res = await fetch(`${BACKEND_URL}/push/register`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getBackendHeaders(authToken?.replace(/^Bearer\s+/i, "")),
      },
      body,
    });

    if (!res.ok) {
      console.warn("[Push] Backend_registration_failed:", await res.text());
      return false;
    }

    console.info("[Push] Successfully registered push subscription");
    return true;
  } catch (err) {
    console.error("[Push] Backend_registration_error:", err);
    return false;
  }
}

// --- Unsubscribe ------------------------------------------------------------

export async function unsubscribeFromPush(authToken: string): Promise<void> {
  if (!("serviceWorker" in navigator)) return;
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return;

  const token = subscription.endpoint;
  await subscription.unsubscribe();

  try {
    await fetch(
      `${BACKEND_URL}/push/unregister?token=${encodeURIComponent(token)}`,
      {
        method: "DELETE",
        headers: getBackendHeaders(authToken?.replace(/^Bearer\s+/i, "")),
      },
    );
  } catch {
    // non-fatal
  }
}

// --- Re-engagement trigger --------------------------------------------------

/**
 * Trigger a re-engagement push notification for all registered users
 * (admin-only). Call from admin panel or server-side jobs.
 */
export async function sendReEngagementPush(
  authToken: string,
  type: "welcome" | "update" | "daily_bonus" | "new_content" = "update",
): Promise<boolean> {
  try {
    const res = await fetch(`${BACKEND_URL}/push/re-engage`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getBackendHeaders(authToken?.replace(/^Bearer\s+/i, "")),
      },
      body: JSON.stringify({ type }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

// --- Local fallback notification --------------------------------------------

/**
 * Show a local (non-push) notification when the app is in focus.
 * Useful for real-time events (new message, reply, etc.).
 */
export async function showLocalNotification(
  title: string,
  body: string,
  url?: string,
): Promise<void> {
  if (!("serviceWorker" in navigator)) return;
  if (Notification.permission !== "granted") return;

  const registration = await navigator.serviceWorker.ready;
  await registration.showNotification(title, {
    body,
    icon: "/uniui_original_remastered_primary.png",
    badge: "/icons/badge-72.png",
    tag: "uniui-local",
    data: { url: url || "/notifications" },
    vibrate: [150, 50, 150],
  } as any);
}
